import { ChangeDetectorRef, Component } from '@angular/core';
import { ModalController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import Article from 'src/smoothr-web-app-core/models/Article';
import Order from 'src/smoothr-web-app-core/models/Order';
import Venue from 'src/smoothr-web-app-core/models/Venue';
import { sleep } from 'src/smoothr-web-app-core/utils/utils';
import { ProductInfoModalComponent } from '../product-info-modal/product-info-modal.component';

export interface SelectedArticleWithQuantity {
	article: Article;
	quantity: number;
}

@Component({
	selector: 'app-selection-multiple-free-articles',
	templateUrl: './selection-multiple-free-articles.component.html',
	styleUrls: ['./selection-multiple-free-articles.component.scss'],
})
export class SelectionMultipleFreeArticlesComponent {
	articlesList: Article[];
	maxQuantity: number;
	order: Order;
	venue: Venue;
	selectedArticles: Map<string, number> = new Map();

	constructor(
		private modalCtrl: ModalController,
		private translate: TranslateService,
		private cdr: ChangeDetectorRef
	) {}

	static async show(
		modalCtrl: ModalController,
		articlesList: Article[],
		order: Order,
		maxQuantity: number,
		venue?: Venue
	): Promise<SelectedArticleWithQuantity[]> {
		const modal = await modalCtrl.create({
			component: SelectionMultipleFreeArticlesComponent,
			cssClass: ['auto-height, recommendation-dialog'],
			componentProps: {
				articlesList,
				order,
				maxQuantity,
				venue,
			},
			showBackdrop: true,
		});
		await modal.present();
		await sleep(100);
		return (await modal.onDidDismiss()).data;
	}

	onInfoIcon(article: Article, event?: Event): void {
		event?.stopPropagation();
		ProductInfoModalComponent.show(this.modalCtrl, article, this.venue, this.order);
	}

	trackBy(index: number, article: Article) {
		return article._id;
	}

	dismiss() {
		this.modalCtrl.dismiss();
	}

	totalSelectedQuantity(): number {
		let total = 0;
		this.selectedArticles.forEach((qty) => (total += qty));
		return total;
	}

	canIncrementQuantity(articleId: string): boolean {
		return this.totalSelectedQuantity() < this.maxQuantity;
	}

	incrementQuantity(article: Article): void {
		if (!this.canIncrementQuantity(article._id)) {
			return;
		}
		const currentQty = this.selectedArticles.get(article._id) || 0;
		this.selectedArticles.set(article._id, currentQty + 1);
		this.cdr.detectChanges();
	}

	decrementQuantity(article: Article): void {
		const currentQty = this.selectedArticles.get(article._id) || 0;
		if (currentQty > 0) {
			this.selectedArticles.set(article._id, currentQty - 1);
			this.cdr.detectChanges();
		}
	}

	getQuantity(articleId: string): number {
		return this.selectedArticles.get(articleId) || 0;
	}

	isAddButtonEnabled(): boolean {
		return this.totalSelectedQuantity() > 0;
	}

	addArticlesToBasket(): void {
		const result: SelectedArticleWithQuantity[] = [];
		this.selectedArticles.forEach((qty, articleId) => {
			if (qty > 0) {
				const article = this.articlesList.find((a) => a._id === articleId);
				if (article) {
					result.push({ article, quantity: qty });
				}
			}
		});
		this.modalCtrl.dismiss(result);
	}
}
